import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import {
  ANNUAL_OKR,
  ANNUAL_OKR_REVIEW,
  EXECUTION_BLUEPRINT_LOCKED_DEFAULT,
  STRATEGY_CANVAS_VIEW,
} from "../../../../data/RouteUrls";
import { HeaderContext } from "../../../../HeaderContext";
import {
  ExternalLinkIcon,
  InfoIcon,
} from "../../StrategyCanvas/StrategyCanvasHelpers";
import { useStrategyCanvasStore } from "../../StrategyCanvas/_redux/StrategyCanvasStore";
import { strategyCanvasUrls } from "../../StrategyCanvas/_redux/StrategyCanvasUrls";
import { useExecutionBlueprintStore } from "../../ExecutionBlueprint/_redux/ExecutionBlueprintStore";
import { executionBlueprintUrls } from "../../ExecutionBlueprint/_redux/ExecutionBlueprintUrls";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import useAnnualOkrStore, {
  useAnnualOkrStoreSelector,
} from "../_redux/AnnualOkrStore";
import useAnnualOkrReviewStore, {
  useAnnualOkrReviewStoreSelector,
} from "./_redux/AnnualOkrReviewStore";
import AnnualOkrReviewMode from "./AnnualOkrReviewMode";
import ReviewInfoModal from "./ReviewInfoModal";
import "./AnnualOkrReview.scss";

// ═══════════════════════════════════════════════════════════════════════════
// The coach's async review pass over a client's Company OKRs (§6.2–6.7).
//
// Screen wrapper modelled on `ExecutionBlueprintReview.js` — same ProjectHeader
// + back, same `#…-review` id wrapper, same header block with the title, the
// reviewed counter and the body below a divider.
//
// ⚠️ NO MODE TOGGLE. EXW has Review Mode / Blueprint Mode because its Blueprint
// Mode is a genuinely different artefact — a wall-chart grid of WIG connections
// with no review UI. Annual OKR's §6.6 `okr-mode` is the SAME list of Objectives
// as §6.3 minus the review controls, so a toggle here would swap the screen for
// a strictly poorer copy of itself. `GET_OKR_MODE` stays declared in the urls
// file against a future read-only surface; nothing calls it.
//
// ⚠️ This header is state-independent, identical before and after the review is
// submitted — the same rule EXW's header carries a comment about. What the
// submitted state changes is the BODY (read-only rows, and the footer CTA).
// ═══════════════════════════════════════════════════════════════════════════

// Persisted the same way as the Execution Blueprint's `ebReviewInfoSeen` and
// Strategy Canvas's `scReviewInfoSeen` — marks that this coach has already seen
// the explainer once, so it auto-opens the very first time any Annual OKR
// review is opened, not on every visit. Per-feature, not per-run: the
// explanation is about how the review works, and it does not change per client.
const REVIEW_INFO_SEEN_KEY = "aokReviewInfoSeen";

const selectClients = (state) => state.clients;
const selectReviewByUniqueId = (state) => state.reviewByUniqueId;

// ⚠️ THE GOLDEN THREAD (JK copy review O22, S1 54:13): the coach reviews these
// OKRs "looking at the strategy canvas, the execution blueprint and these
// company OKRs". The Annual OKR payloads carry neither id, so both are looked
// up by company (`company_id`) from their own coach dashboards — the same trick
// EXW's review uses for its "View Strategy Canvas" link. Cached rows are used
// when the coach came through those screens; otherwise one silent GET each.
// Silent on purpose: these are convenience links, so a failed lookup just means
// the link doesn't appear, never an error toast.
const useLinkedArtefactIds = (companyId) => {
  const [ids, setIds] = useState({ canvasId: null, blueprintId: null });
  useEffect(() => {
    if (companyId == null) return undefined;
    let cancelled = false;
    const merge = (patch) => {
      if (!cancelled) setIds((prev) => ({ ...prev, ...patch }));
    };

    const scCached = (useStrategyCanvasStore.getState().clients || []).find(
      (c) => c.id === companyId,
    );
    if (scCached?.briefId) {
      merge({ canvasId: scCached.briefId });
    } else {
      useStrategyCanvasStore
        .getState()
        ._get(strategyCanvasUrls.GET_DASHBOARD)
        .then((response) => {
          const row = (response.data?.data?.clients || []).find(
            (c) => c.company_id === companyId,
          );
          merge({ canvasId: row?.canvas_unique_id || null });
        })
        .catch(() => {});
    }

    const ebCached = (useExecutionBlueprintStore.getState().clients || []).find(
      (c) => c.id === companyId,
    );
    if (ebCached?.blueprintId) {
      merge({ blueprintId: ebCached.blueprintId });
    } else {
      useExecutionBlueprintStore
        .getState()
        ._get(executionBlueprintUrls.GET_DASHBOARD)
        .then((response) => {
          const row = (response.data?.data?.clients || []).find(
            (c) => c.company_id === companyId,
          );
          merge({ blueprintId: row?.execution_blueprint_unique_id || null });
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [companyId]);
  return ids;
};

// Opens in a new tab so the review stays where the coach left it.
const openInNewTab = (url) => window.open(url, "_blank", "noopener,noreferrer");

const AnnualOkrReview = () => {
  const history = useHistory();
  const { annualOkrId } = useParams();
  const dispatch = useDispatch();

  const clients = useAnnualOkrStoreSelector(selectClients);
  const client = (clients || []).find((c) => c.annualOkrId === annualOkrId);
  const companyName = client?.name || "";
  const { canvasId, blueprintId } = useLinkedArtefactIds(client?.id);

  const reviewByUniqueId = useAnnualOkrReviewStoreSelector(
    selectReviewByUniqueId,
  );
  const review = reviewByUniqueId[annualOkrId];
  const reviewedCount = review?.reviewed_count ?? 0;
  const totalObjectives = review?.total_objectives ?? null;

  const [showInfoModal, setShowInfoModal] = useState(false);

  const HeaderMetaData = useContext(HeaderContext);

  useEffect(() => {
    document.title = "Art of Scale | Annual OKR Review";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Annual OKRs" });
    // The dashboard supplies the company name for the breadcrumb. Fetched here
    // too because the coach can land on this screen directly — a bookmark, a
    // back/forward, or the link in the notification email — with no dashboard
    // behind them. Same reasoning EXW's review screen gives for its own repeat
    // calls.
    if (!useAnnualOkrStore.getState().clients) {
      useAnnualOkrStore.getState().fetchDashboard();
    }
    // §6.2 is idempotent while the run is in review, so calling it on every
    // entry is safe and saves the dashboard button from having to.
    useAnnualOkrReviewStore.getState().enterReview(annualOkrId);
    useAnnualOkrReviewStore.getState().fetchReview(annualOkrId);
    // eslint-disable-next-line
  }, [annualOkrId]);

  // ⚠️ The company name is an INERT crumb (`url: ""`), matching EXW's. A crumb
  // with no url renders as text rather than a link — `ProjectHeader` handles
  // that explicitly, because `<Link to="">` resolves to the app root and would
  // quietly send the coach home.
  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(ANNUAL_OKR_REVIEW, [
        breadCrumbsArray[ANNUAL_OKR][0],
        { title: companyName || "Annual OKR review", url: "" },
      ]),
    );
  }, [dispatch, companyName]);

  // First-time onboarding — pop the explainer open on its own the very first
  // time a coach reaches this screen, rather than relying on them noticing the
  // info affordance in the header. Same approach as both sibling features.
  useEffect(() => {
    if (!localStorage.getItem(REVIEW_INFO_SEEN_KEY)) {
      setShowInfoModal(true);
      localStorage.setItem(REVIEW_INFO_SEEN_KEY, "true");
    }
  }, []);

  return (
    <div>
      <ProjectHeader
        handleNavigation={() => history.push(ANNUAL_OKR)}
        showBack
      />
      <div id="annual-okr-review">
        <div className="aokr-header">
          <div className="aokr-title">
            {/* One name for the feature on the coach side, as EXW's header
                ("Execution Blueprint — {client}") does (G37). */}
            Annual OKRs{companyName ? ` — ${companyName}` : ""}
          </div>
          <div className="aokr-header-right">
            <button
              type="button"
              className="aokr-info-icon"
              onClick={() => setShowInfoModal(true)}
            >
              <InfoIcon size={14} />
              <span>How review works</span>
            </button>

            {canvasId ? (
              <button
                type="button"
                className="aokr-info-icon"
                onClick={() =>
                  openInNewTab(
                    STRATEGY_CANVAS_VIEW.replace(":briefId", canvasId),
                  )
                }
              >
                <span>View Strategy Canvas</span>
                <ExternalLinkIcon size={14} />
              </button>
            ) : null}

            {/* The locked Blueprint — Annual OKRs can only start from one. */}
            {blueprintId ? (
              <button
                type="button"
                className="aokr-info-icon"
                onClick={() =>
                  openInNewTab(
                    EXECUTION_BLUEPRINT_LOCKED_DEFAULT.replace(
                      ":blueprintId",
                      blueprintId,
                    ),
                  )
                }
              >
                <span>View Execution Blueprint</span>
                <ExternalLinkIcon size={14} />
              </button>
            ) : null}

            {totalObjectives !== null ? (
              <div className="aokr-reviewed">
                Reviewed:
                <strong>
                  {" "}
                  {reviewedCount} of {totalObjectives}
                </strong>
              </div>
            ) : null}
          </div>
        </div>
        <div className="aokr-divider" />

        <AnnualOkrReviewMode uniqueId={annualOkrId} />
      </div>

      {showInfoModal && (
        <ReviewInfoModal onClose={() => setShowInfoModal(false)} />
      )}
    </div>
  );
};

export default AnnualOkrReview;
